import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  TrackByFunction,
  viewChild,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { without } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { concatMap, tap } from 'rxjs/operators';
import {
  AutoFocusDirective,
  ConfirmModalComponent,
  ConfirmModalData,
  CopyWhistleDirective,
  PopOverService,
  Tag,
  TagInputComponent,
  TagListComponent,
  TeamMemberRolePipe,
} from '@entscheidungsnavi/widgets';
import { DatePipe } from '@angular/common';
import {
  ProjectDto,
  ProjectFoldersService,
  ProjectHierarchyNode,
  ProjectHierarchyNodeFolder,
  ProjectHierarchyNodeOnlineProject,
  ProjectHierarchyNodeProject,
  ProjectHierarchyNodeQuickstartProject,
  ProjectHierarchyNodeTeamProject,
  QuickstartProjectDto,
  QuickstartTagDto,
} from '@entscheidungsnavi/api-client';
import { OverlayProgressBarDirective } from '@entscheidungsnavi/widgets/overlay-progress-bar';
import { ProjectFolderDto, TeamProjectInfoWithRoleDto } from '@entscheidungsnavi/api-types';
import { MatNoDataRow, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { assertUnreachable } from '@entscheidungsnavi/tools';
import { concat, lastValueFrom, noop, Observable, of } from 'rxjs';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { TreeMenuComponent } from '@entscheidungsnavi/widgets/tree-menu';
import { plainToClass } from 'class-transformer';
import { ProjectLinkModalComponent } from '../project-link-modal/project-link-modal.component';
import { LocalProject, OnlineProject, ProjectService } from '../../../data/project';
import { TeamProject } from '../../../data/project/team-project';
import { OnlineProjectManagementService } from '../project-management.service';

type ProjectType = QuickstartProjectDto | ProjectDto | TeamProjectInfoWithRoleDto;

@Component({
  selector: 'dt-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  hostDirectives: [OverlayProgressBarDirective],
  standalone: true,
  imports: [
    MatButton,
    MatIconButton,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatFormField,
    MatLabel,
    MatInput,
    MatSortModule,
    MatTableModule,
    MatTooltip,
    FormsModule,
    TagInputComponent,
    OverlayProgressBarDirective,
    AutoFocusDirective,
    TagListComponent,
    DatePipe,
    TeamMemberRolePipe,
    CdkCopyToClipboard,
    CopyWhistleDirective,
    MatSuffix,
    MatNoDataRow,
    AutoFocusDirective,
    TreeMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectListComponent {
  projectService = inject(ProjectService);
  currentlyLoadedProject = toSignal(this.projectService.project$);

  projects = model<readonly ProjectType[]>();
  projectFolders = model<readonly ProjectFolderDto[]>();
  tags = input<readonly Tag[]>([]);
  quickstart = input(false);
  shouldLoadProjects = input(true);

  highlightedProjectName = model<string | null>(null);
  filterText = model<string>('');
  currentFolderId = model<string>(null);
  filterTagIds = model<readonly string[]>([]);

  startInLoadedProjectsFolder = signal(true);

  loadedProject = output<ProjectType>();

  newElementId = signal<string>(null);

  sort = viewChild(MatSort);

  currentlyLoadedProjectId = computed(() => {
    const loadedProject = this.currentlyLoadedProject();

    if (!loadedProject || loadedProject instanceof LocalProject) {
      return null;
    }

    if (loadedProject instanceof TeamProject) {
      return loadedProject.team.id;
    } else if (loadedProject instanceof OnlineProject) {
      return loadedProject.info.id;
    } else {
      assertUnreachable(loadedProject);
    }
  });

  currentlyLoadedHierarchyNode = computed(() => {
    return this.projectHierarchy().allProjects.find(f => f.id === this.currentlyLoadedProjectId());
  });

  tagsById = computed(() => {
    if (!this.tags()) {
      return {};
    }

    const acc: { [id: string]: QuickstartTagDto } = {};
    return this.tags().reduce((acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    }, acc);
  });

  projectHierarchy = computed(() => {
    return this.projectFoldersService.generateProjectHierarchy(this.projectFolders(), this.projects(), $localize`Meine Projekte`);
  });

  displayedColumns = computed(() => {
    const columns: string[] = ['type', 'name'];

    if (this.quickstart()) {
      columns.push('tags');
    } else {
      if (this.filterText()) {
        columns.push('path');
      }

      columns.push('date');
    }

    columns.push('quickactions');

    if (!this.quickstart()) {
      columns.push('actions');
    }

    return columns;
  });

  currentFolder = computed(() => {
    return this.projectHierarchy().allFolders.find(f => f.id === this.currentFolderId()) ?? this.projectHierarchy().root;
  });

  parentHierarchy = computed(() => {
    return [...this.currentFolder().path, this.currentFolder()];
  });

  userHasElements = computed(() => {
    return this.projects().length > 0 || this.projectFolders().length > 0;
  });

  loadedHierarchyIds = computed(() => {
    const currentlyLoadedNode = this.currentlyLoadedHierarchyNode();

    if (!currentlyLoadedNode) {
      return new Set();
    }

    return new Set([...currentlyLoadedNode.path, currentlyLoadedNode].map(node => node.id));
  });

  dataSource: MatTableDataSource<ProjectHierarchyNode>;

  @ViewChild('newFolderButton')
  newFolderButton: MatButton;

  @ViewChild('newFolderTemplate')
  newFolderTemplate: TemplateRef<unknown>;

  currentlyRenamingElement: ProjectHierarchyNodeFolder | ProjectHierarchyNodeOnlineProject | null = null;

  @ViewChild(MatTable)
  table: MatTable<ProjectHierarchyNode>;

  currentlyDraggingInto: ProjectHierarchyNodeFolder | null = null;
  currentlyDragging: ProjectHierarchyNode | null = null;

  constructor(
    private dialog: MatDialog,
    private date: DatePipe,
    private projectFoldersService: ProjectFoldersService,
    private popOverService: PopOverService,
    private cdRef: ChangeDetectorRef,
    private projectManagementService: OnlineProjectManagementService,
    private overlayProgress: OverlayProgressBarDirective,
  ) {
    this.dataSource = new MatTableDataSource();

    const startInLoadedProjectsFolder = effect(() => {
      if (this.startInLoadedProjectsFolder()) {
        const currentlyLoadedNode = this.currentlyLoadedHierarchyNode();
        if (currentlyLoadedNode && currentlyLoadedNode.parent !== this.projectHierarchy().root) {
          this.currentFolderId.set(currentlyLoadedNode.parent.id);
        }
      }

      startInLoadedProjectsFolder.destroy();
    });

    effect(() => {
      this.dataSource.sort = this.sort();
    });

    effect(() => {
      this.dataSource.filter = this.filterText();
    });

    effect(() => {
      if (this.filterText()) {
        this.dataSource.data = this.currentFolder().deepProjectChildren;
      } else {
        this.dataSource.data = this.currentFolder().children;
      }
    });

    this.dataSource._filterData = (data: ProjectHierarchyNode[]) => {
      this.dataSource.filteredData =
        (this.dataSource.filter == null || this.dataSource.filter === '') && this.filterTagIds().length === 0
          ? data
          : data.filter(obj => this.dataSource.filterPredicate(obj, this.dataSource.filter));

      if (this.dataSource.paginator) {
        this.dataSource._updatePaginator(this.dataSource.filteredData.length);
      }

      this.tags()?.forEach(
        tag =>
          (tag.isProductive = this.dataSource.filteredData.some(
            p => p instanceof ProjectHierarchyNodeQuickstartProject && p.value.tags.includes(tag.id),
          )),
      );

      return this.dataSource.filteredData;
    };

    const originalPredicate = this.dataSource.filterPredicate;

    this.dataSource.filterPredicate = (data, filter) => {
      let isMatch = originalPredicate({ name: data.name } as any, filter);

      if (this.quickstart && isMatch) {
        isMatch &&=
          !(data instanceof ProjectHierarchyNodeQuickstartProject) || this.filterTagIds().every(tagId => data.value.tags.includes(tagId));
      }

      return isMatch;
    };

    // Sort based on type
    this.dataSource.sortingDataAccessor = (element, sortHeaderId) => {
      let dataToSortBy = null;
      if (sortHeaderId === 'type') {
        dataToSortBy = this.isFolderNode(element) ? 0 : this.isTeamProjectNode(element) ? 1 : 2;
      } else {
        dataToSortBy = (element as any)[sortHeaderId];
      }

      if (dataToSortBy instanceof Date) {
        dataToSortBy = dataToSortBy.getTime();
      }

      return (dataToSortBy + element.name).toLocaleLowerCase();
    };
  }

  trackTableElements: TrackByFunction<ProjectHierarchyNode> = (index, item) => item.id;

  changedTagFilter() {
    this.dataSource.data = this.dataSource.data.slice();
    this.table.renderRows();
    this.cdRef.detectChanges();
  }

  private generateDeleteObservableForFolderDelete(folder: ProjectHierarchyNodeFolder) {
    const deleteObservables: Observable<void>[] = [];

    for (const child of folder.deepProjectChildren) {
      if (this.isOnlineProjectNode(child)) {
        deleteObservables.push(this.projectManagementService.delete(child.id));
      }
    }

    for (const child of folder.deepFolderChildren) {
      deleteObservables.push(this.projectFoldersService.deleteProjectFolder(child.id));
    }

    deleteObservables.push(this.projectFoldersService.deleteProjectFolder(folder.id));

    return concat(...deleteObservables);
  }

  protected async deleteElement(element: ProjectHierarchyNodeOnlineProject | ProjectHierarchyNodeFolder, htmlElement: HTMLElement) {
    const deletingProject = this.isOnlineProjectNode(element);

    const title = deletingProject ? $localize`Projekt Löschen` : $localize`Ordner Löschen`;
    let prompt = deletingProject
      ? $localize`Bist Du sicher, dass Du das Projekt „${element.name}“ vom ${this.date.transform(element.updatedAt)} löschen möchten?`
      : $localize`Bist Du sicher, dass Du den Ordner „${element.name}“ und alle darin enthaltenen Projekte und Ordner löschen möchtest?`;

    let listElements: string[] = null;

    if (!deletingProject) {
      const containsTeamProjects = element.deepProjectChildren.some(this.isTeamProjectNode);
      const containsOnlineProjects = element.deepProjectChildren.some(this.isOnlineProjectNode);

      if (containsTeamProjects) {
        prompt += ' ' + $localize`Teamprojekte werden NICHT gelöscht und stattdessen in den übergeordneten Ordner verschoben.`;
      }

      if (containsOnlineProjects) {
        prompt += '\n' + $localize`Die folgenden Online-Projekte werden gelöscht:`;

        listElements = element.deepProjectChildren.filter(this.isOnlineProjectNode).map(p => p.name);
      }
    }

    const confirmed = await lastValueFrom(
      this.dialog
        .open<ConfirmModalComponent, ConfirmModalData>(ConfirmModalComponent, {
          data: {
            title,
            prompt,
            template: 'delete',
            listElements,
          },
        })
        .afterClosed(),
    );

    if (!confirmed) {
      return;
    }

    let deleteObservable: Observable<void>;

    if (deletingProject) {
      deleteObservable = this.projectManagementService
        .delete(element.id)
        .pipe(tap({ complete: () => this.projects.update(projects => projects.filter(p => p.id !== element.id)) }));
    } else {
      deleteObservable = this.generateDeleteObservableForFolderDelete(element).pipe(
        tap({
          complete: () => {
            const deletedFolderIds = new Set([element.id, ...element.deepFolderChildren.map(f => f.id)]);
            const deletedProjectIds = new Set(element.deepProjectChildren.filter(this.isOnlineProjectNode).map(p => p.id));
            const movedTeamProjects = element.deepProjectChildren.filter(this.isTeamProjectNode);

            element.parent.value?.children.push(...movedTeamProjects.map(p => p.id));

            // Remove deleted folders
            this.projectFolders.update(folders => folders.filter(f => !deletedFolderIds.has(f.id)));

            // Remove deleted projects
            this.projects.update(projects => projects.filter(p => !deletedProjectIds.has(p.id)));
          },
        }),
      );
    }

    deleteObservable.pipe(this.overlayProgress.reactivePipe()).subscribe({
      error: () => {
        this.popOverService.whistle(htmlElement, $localize`Fehler beim Löschen des Ordners`, 'error');
      },
    });
  }

  protected openNewFolderPopover() {
    this.popOverService.open(this.newFolderTemplate, this.newFolderButton._elementRef, {
      position: [
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
        },
      ],
    });
  }

  protected renameElement(newName: string, htmlElement: HTMLElement) {
    const renamingElement = this.currentlyRenamingElement;
    this.currentlyRenamingElement = null;

    const renamingProject = this.isOnlineProjectNode(renamingElement);

    if (newName.trim() === '' || newName === renamingElement.name) {
      return;
    }

    if (renamingProject && this.projects().some(p => p instanceof ProjectDto && p.name === newName)) {
      this.popOverService.whistle(htmlElement, $localize`Ein Projekt mit diesem Namen existiert bereits`, 'error');
      return;
    }

    let renameObservable: Observable<void> = null;

    if (renamingProject) {
      renameObservable = this.projectManagementService.rename(renamingElement.id, newName);
    } else {
      renameObservable = this.projectFoldersService.updateProjectFolder(renamingElement.id, newName);
    }

    if (renameObservable) {
      renameObservable.pipe(this.overlayProgress.reactivePipe()).subscribe({
        next: () => {
          renamingElement.value.name = newName;

          if (renamingProject) {
            this.projects.update(projects => projects.slice());
          } else {
            this.projectFolders.update(folders => folders.slice());
          }
        },
        error: () => {
          this.popOverService.whistle(htmlElement, $localize`Fehler beim Umbenennen des Projekts`, 'error');
        },
      });
    }
  }

  protected createFolderInCurrentFolder(folderName: string) {
    this.projectFoldersService.createProjectFolder(folderName, this.currentFolder().id).subscribe({
      next: createdFolder => {
        this.currentFolder().value?.children.push(createdFolder.id);

        this.newElementId.set(createdFolder.id);
        this.projectFolders.update(folders => [...folders, createdFolder]);
      },
      error: () => {
        this.popOverService.whistle(this.newFolderButton._elementRef.nativeElement, $localize`Fehler beim Erstellen des Ordners`, 'error');
      },
    });
  }

  protected loadProject(projectNode: ProjectHierarchyNodeProject, createCopy = false) {
    if (!this.shouldLoadProjects()) {
      this.loadedProject.emit(projectNode.value);
      return;
    }

    const loadObservable = this.isQuickstartProjectNode(projectNode)
      ? this.projectService.loadQuickstartProject(projectNode.id)
      : this.isTeamProjectNode(projectNode)
        ? this.projectService.loadTeamProject(projectNode.id, { loadAsLocal: createCopy })
        : this.projectService.loadOnlineProject(projectNode.id, { loadAsLocal: createCopy });

    loadObservable.subscribe({
      next: success => {
        if (!success) return;

        if (createCopy) {
          const project = this.projectService.getProject(LocalProject);
          project.rename(project.name + ' ' + $localize`(Kopie)`).subscribe();
        }

        this.loadedProject.emit(projectNode.value);
      },
      error: noop,
    });
  }

  protected moveIntoFolder(toMove: ProjectHierarchyNode, target: ProjectHierarchyNodeFolder, htmlElement: HTMLElement) {
    const srcFolder = toMove.parent;

    this.projectFoldersService.moveIntoFolder(toMove.id, target.id).subscribe({
      next: () => {
        srcFolder.value?.children.splice(
          srcFolder.value.children.findIndex(id => id === toMove.id),
          1,
        );

        target.value?.children.push(toMove.id);

        this.newElementId.set(toMove.id);
        this.projectFolders.update(folders => folders.slice());
      },
      error: () => {
        this.popOverService.whistle(htmlElement, $localize`Fehler beim Verschieben des Projekts`, 'error');
      },
    });
  }

  protected createTeam(project: ProjectHierarchyNodeOnlineProject, htmlElement: HTMLElement) {
    const targetFolder = project.parent;

    this.projectManagementService
      .createTeamFromOnlineProject(project.id, $localize`${project.name} (Team)`)
      .pipe(
        tap({
          next: team => {
            const teamWithRole: TeamProjectInfoWithRoleDto = plainToClass(TeamProjectInfoWithRoleDto, { ...team, userMemberRole: 'owner' });

            this.newElementId.set(teamWithRole.id);
            this.projects.update(projects => [...projects, teamWithRole]);
          },
        }),
        concatMap(team => {
          let observable: Observable<void>;

          if (targetFolder === this.projectHierarchy().root) {
            observable = of();
          } else {
            observable = this.projectFoldersService.moveIntoFolder(team.id, targetFolder.id);
          }

          return observable.pipe(
            tap({
              complete: () => {
                targetFolder.value?.children.push(team.id);

                this.projectFolders.update(folders => folders.slice());
              },
            }),
          );
        }),
      )
      .subscribe({
        error: () => {
          this.popOverService.whistle(htmlElement, $localize`Fehler beim Erstellen des Teamprojekts`, 'error');
        },
      });
  }

  protected clickRow(row: ProjectHierarchyNode) {
    if (this.isFolderNode(row)) {
      this.changeFolder(row);
    } else {
      this.loadProject(row);
    }
  }

  protected leaveTeam(teamNode: ProjectHierarchyNodeTeamProject, htmlElement: HTMLElement) {
    this.projectManagementService
      .leaveTeam(teamNode.id)
      .pipe(this.overlayProgress.reactivePipe())
      .subscribe({
        next: () => {
          this.projects.update(projects => projects.filter(pr => pr.id !== teamNode.id));
        },
        error: () => {
          this.popOverService.whistle(
            htmlElement,
            $localize`Das Team konnte nicht verlassen werden. Als letztes echtes Teammitglied kannst Du das Team nur löschen.`,
            'error',
          );
        },
      });
  }

  protected createProjectLink(projectNode: ProjectHierarchyNodeOnlineProject) {
    this.dialog.open(ProjectLinkModalComponent, { data: { project: projectNode.value } });
  }

  protected onTagClick(tag: QuickstartTagDto) {
    if (!this.filterTagIds().includes(tag.id)) {
      this.filterTagIds.update(oldTags => [...oldTags, tag.id]);
    } else {
      this.filterTagIds.update(oldTags => without(oldTags, tag.id));
    }

    this.changedTagFilter();
  }

  protected changeFolder(folderNode: ProjectHierarchyNodeFolder) {
    this.currentFolderId.set(folderNode.id);
  }

  protected clearFilterText() {
    this.filterText.set('');
  }

  protected getDisplayPath(node: ProjectHierarchyNode): string {
    return node.path.map(f => f.name).join(' / ');
  }

  protected isFolderNode(node: ProjectHierarchyNode): node is ProjectHierarchyNodeFolder {
    return node instanceof ProjectHierarchyNodeFolder;
  }

  protected isProjectNode(node: ProjectHierarchyNode): node is ProjectHierarchyNodeProject {
    return !this.isFolderNode(node);
  }

  protected isOnlineProjectNode(node: ProjectHierarchyNode): node is ProjectHierarchyNodeOnlineProject {
    return node instanceof ProjectHierarchyNodeOnlineProject;
  }

  protected isQuickstartProjectNode(node: ProjectHierarchyNode): node is ProjectHierarchyNodeQuickstartProject {
    return node instanceof ProjectHierarchyNodeQuickstartProject;
  }

  protected isTeamProjectNode(node: ProjectHierarchyNode): node is ProjectHierarchyNodeTeamProject {
    return node instanceof ProjectHierarchyNodeTeamProject;
  }

  protected canBeRenamed(element: ProjectHierarchyNode): element is ProjectHierarchyNodeOnlineProject | ProjectHierarchyNodeFolder {
    return this.isOnlineProjectNode(element) || this.isFolderNode(element);
  }

  protected canBeDeleted(element: ProjectHierarchyNode): element is ProjectHierarchyNodeOnlineProject | ProjectHierarchyNodeFolder {
    return this.isOnlineProjectNode(element) || this.isFolderNode(element);
  }

  // Drag and Drop Logic
  protected dropOnElement(event: DragEvent, droppedOn: ProjectHierarchyNode) {
    this.currentlyDraggingInto = null;

    if (this.isFolderNode(droppedOn)) {
      const id = event.dataTransfer.getData('text/id');

      const toMove =
        this.projectHierarchy().root.deepProjectChildren.find(f => f.id === id) ??
        this.projectHierarchy().root.deepFolderChildren.find(f => f.id === id);

      if (!toMove) {
        return;
      }

      this.moveIntoFolder(toMove, droppedOn, event.target as HTMLElement);
      event.preventDefault();
    }
  }

  protected dragOverElement(event: DragEvent, draggedOver: ProjectHierarchyNode) {
    if (this.currentlyDragging && this.isFolderNode(draggedOver) && draggedOver !== this.currentlyDragging) {
      event.preventDefault();
      this.currentlyDraggingInto = draggedOver;
    }
  }

  protected dragElement(event: DragEvent, dragging: ProjectHierarchyNode) {
    event.dataTransfer.setData('text/id', dragging.id);
    this.currentlyDragging = dragging;
  }

  protected stopDrag() {
    this.currentlyDragging = null;
  }

  protected dragEnterElement(event: DragEvent, entered: ProjectHierarchyNode) {
    if (this.currentlyDragging && this.isFolderNode(entered) && entered !== this.currentlyDragging) {
      this.currentlyDraggingInto = entered;
    }
  }

  protected dragLeaveElement(event: DragEvent, leaving: ProjectHierarchyNode) {
    if (this.currentlyDraggingInto === leaving) {
      this.currentlyDraggingInto = null;
    }
  }
}
